import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { X, Link2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export default function TaskDependencies({ dependencies = [], availableTasks = [], onChange, currentTaskId }) {
  const handleAddDependency = (taskId) => {
    if (!taskId || dependencies.includes(taskId)) return;
    onChange([...dependencies, taskId]);
  };

  const handleRemoveDependency = (taskId) => {
    onChange(dependencies.filter(id => id !== taskId));
  };

  // Filter ut gjeldende oppgave og underoppgaver
  const selectableTasks = availableTasks.filter(t => 
    t.id !== currentTaskId && 
    t.parent_task_id !== currentTaskId &&
    !dependencies.includes(t.id)
  );

  const dependentTasks = dependencies
    .map(id => availableTasks.find(t => t.id === id))
    .filter(Boolean);

  // Sjekk om noen avhengigheter er blokkerende
  const blockedBy = dependentTasks.filter(t => t.status !== "completed");

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Link2 className="w-4 h-4" />
          Avhengigheter
        </Label>
        {blockedBy.length > 0 && (
          <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">
            <AlertCircle className="w-3 h-3 mr-1" />
            {blockedBy.length} blokkerer
          </Badge>
        )}
      </div>

      {/* Eksisterende avhengigheter */}
      {dependentTasks.length > 0 && (
        <div className="space-y-2">
          {dependentTasks.map((task) => {
            const isBlocking = task.status !== "completed";
            return (
              <div
                key={task.id}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg border",
                  isBlocking ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"
                )}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{task.title}</p>
                  <p className="text-xs text-slate-500">
                    Status: {task.status === "completed" ? "✓ Fullført" : "⏳ Pågår"}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleRemoveDependency(task.id)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Legg til ny avhengighet */}
      {selectableTasks.length > 0 && (
        <Select onValueChange={handleAddDependency}>
          <SelectTrigger className="h-11">
            <SelectValue placeholder="Legg til avhengighet..." />
          </SelectTrigger>
          <SelectContent>
            {selectableTasks.map((task) => (
              <SelectItem key={task.id} value={task.id}>
                <div className="flex flex-col">
                  <span className="font-medium">{task.title}</span>
                  <span className="text-xs text-slate-500">
                    {task.phase_name || "Ingen fase"} • {task.status}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {dependencies.length === 0 && (
        <p className="text-sm text-slate-500 italic">
          Ingen avhengigheter. Denne oppgaven kan startes når som helst.
        </p>
      )}

      {blockedBy.length > 0 && (
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-xs text-amber-800">
            ⚠️ Denne oppgaven er blokkert av {blockedBy.length} ufullført{blockedBy.length > 1 ? 'e' : ''} oppgave{blockedBy.length > 1 ? 'r' : ''}
          </p>
        </div>
      )}
    </div>
  );
}